/**
 * 浏览器语音合成（离线回退方案）
 * ============================================================
 * 正常情况下课程发音由后端 Qwen3-TTS 提供（音质好、是真人味的粤语）。
 * 但 TTS 需要云端密钥，密钥失效时学习者就完全听不到发音了——这对语言学习是致命的。
 *
 * 所以这里提供一层浏览器内置语音合成的回退：
 *   Web Speech API 里的 zh-HK 声音（Windows 上通常是 Microsoft Tracy / HonHing）
 *   可以直接朗读粤语文本，虽然不如 Qwen3-TTS 自然，但至少能听、能跟读。
 *
 * 调用方应当把它当作**回退**：后端 TTS 可用时优先用后端的。
 *
 * ⚠️ 已知局限：
 *   1. 不同设备/浏览器的粤语音色质量差异很大，有的设备根本没有 zh-HK 声音；
 *   2. 粤语口语字（嘅咗喺唔啲）在部分引擎里会被按普通话读，发音可能不准；
 *   3. iOS Safari 需要用户手势触发才能发声。
 *   因此这里会把"实际用的声音"回报给调用方，让 UI 能如实提示用户。
 */

/** 粤语音色候选（按优先级） */
const CANTONESE_VOICE_HINTS = [
  /zh[-_]HK/i, // 标准粤语标记
  /yue/i,      // 部分引擎用 yue 表示粤语
  /cantonese/i,
  /hong\s*kong/i,
];

/** 退而求其次：至少是中文 */
const CHINESE_VOICE_HINTS = [/zh[-_]CN/i, /zh[-_]TW/i, /^zh/i, /chinese/i, /中文/i];

let cachedVoice = null;
let voicesReady = false;

/**
 * 检查当前环境是否支持语音合成
 * @returns {boolean}
 */
export function isSpeechSynthesisAvailable() {
  return typeof window !== 'undefined' && 'speechSynthesis' in window;
}

/**
 * 取得所有可用声音（处理异步加载）
 * @returns {Promise<SpeechSynthesisVoice[]>}
 */
function loadVoices() {
  return new Promise((resolve) => {
    if (!isSpeechSynthesisAvailable()) return resolve([]);

    const existing = window.speechSynthesis.getVoices();
    if (existing && existing.length > 0) {
      voicesReady = true;
      return resolve(existing);
    }

    // 声音列表在部分浏览器里是异步填充的，需要等 voiceschanged
    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      voicesReady = true;
      resolve(window.speechSynthesis.getVoices() || []);
    };

    window.speechSynthesis.addEventListener?.('voiceschanged', finish, { once: true });
    // 兜底定时器，避免某些浏览器不触发事件
    setTimeout(finish, 800);
  });
}

/**
 * 找一个粤语音色；找不到则回退到任意中文音色
 * @returns {Promise<{ voice: SpeechSynthesisVoice|null, kind: 'cantonese'|'chinese'|'none' }>}
 */
export async function resolveVoice() {
  if (!isSpeechSynthesisAvailable()) return { voice: null, kind: 'none' };

  if (cachedVoice) {
    const isCantonese = CANTONESE_VOICE_HINTS.some((re) => re.test(cachedVoice.lang) || re.test(cachedVoice.name));
    return { voice: cachedVoice, kind: isCantonese ? 'cantonese' : 'chinese' };
  }

  const voices = await loadVoices();
  if (!voices.length) return { voice: null, kind: 'none' };

  const find = (hints) =>
    voices.find((v) => hints.some((re) => re.test(v.lang || '') || re.test(v.name || '')));

  const cantonese = find(CANTONESE_VOICE_HINTS);
  if (cantonese) {
    cachedVoice = cantonese;
    return { voice: cantonese, kind: 'cantonese' };
  }

  const chinese = find(CHINESE_VOICE_HINTS);
  if (chinese) {
    cachedVoice = chinese;
    return { voice: chinese, kind: 'chinese' };
  }

  return { voice: null, kind: 'none' };
}

/**
 * 朗读一段粤语文本
 * @param {string} text - 要朗读的文本
 * @param {Object} [options]
 * @param {number} [options.rate=0.9] - 语速（默认稍慢，便于跟读）
 * @returns {Promise<{ spoken: boolean, kind: string, reason?: string }>}
 */
export async function speak(text, { rate = 0.9 } = {}) {
  if (!text) return { spoken: false, kind: 'none', reason: 'empty_text' };
  if (!isSpeechSynthesisAvailable()) {
    return { spoken: false, kind: 'none', reason: 'unsupported' };
  }

  const { voice, kind } = await resolveVoice();
  if (!voice) {
    return { spoken: false, kind: 'none', reason: 'no_voice' };
  }

  return new Promise((resolve) => {
    try {
      window.speechSynthesis.cancel(); // 打断上一句，避免叠读

      const utter = new SpeechSynthesisUtterance(text);
      utter.voice = voice;
      utter.lang = voice.lang || 'zh-HK';
      utter.rate = rate;
      utter.pitch = 1;
      utter.onend = () => resolve({ spoken: true, kind });
      utter.onerror = () => resolve({ spoken: false, kind, reason: 'speak_error' });

      window.speechSynthesis.speak(utter);
    } catch (e) {
      resolve({ spoken: false, kind, reason: e?.message || 'exception' });
    }
  });
}

/**
 * 停止当前朗读
 */
export function stopSpeaking() {
  if (isSpeechSynthesisAvailable()) {
    try {
      window.speechSynthesis.cancel();
    } catch (e) {
      /* 忽略 */
    }
  }
}

/**
 * 仅供测试：清空音色缓存
 * 音色是缓存的（避免每次朗读都重新遍历声音列表），
 * 但测试需要在不同音色配置下重复调用 resolveVoice()。
 */
export function __resetVoiceCacheForTest() {
  cachedVoice = null;
  voicesReady = false;
}
